import * as THREE from 'three';

/**
 * The predicted orbital path, drawn as a line through the same points
 * `TrajectoryPredictor` samples for the HUD's event list — one computation,
 * two presentations of it.
 *
 * Colour is the primary signal, set by the caller from the prediction's
 * `escape`/`impacts` flags: a calm colour for a stable orbit, a warning
 * colour for one that leaves the sphere of influence or hits the ground,
 * so the shape of the trouble is visible before the player reads a number.
 */
export class TrajectoryLine {
  readonly line: THREE.Line;
  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.LineBasicMaterial;
  private capacity = 0;

  constructor() {
    this.geometry = new THREE.BufferGeometry();
    this.material = new THREE.LineBasicMaterial({
      color: 0x7fd1ff,
      transparent: true,
      opacity: 0.8,
      depthWrite: false,
    });
    this.line = new THREE.Line(this.geometry, this.material);
    this.line.frustumCulled = false;
  }

  setPath(points: THREE.Vector3[], color: number): void {
    if (points.length < 2) {
      this.line.visible = false;
      return;
    }
    this.line.visible = true;
    this.material.color.setHex(color);

    if (this.capacity !== points.length) {
      this.geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(points.length * 3), 3));
      this.capacity = points.length;
    }
    const attr = this.geometry.getAttribute('position') as THREE.BufferAttribute;
    for (let i = 0; i < points.length; i++) attr.setXYZ(i, points[i].x, points[i].y, points[i].z);
    attr.needsUpdate = true;
    this.geometry.setDrawRange(0, points.length);
    this.geometry.computeBoundingSphere();
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}
